import { Room, RoomEvent, Track, ConnectionState } from 'livekit-client';
import { fetchLiveKitToken } from '../../../utils/livekitService';

const PUBLIC = (typeof process !== 'undefined' && process.env.PUBLIC_URL) || '';


// ═══════════════════════════════════════════════════════════════
//  PRESET STATES
// ═══════════════════════════════════════════════════════════════
const STATES = {
  neutral: {
    outerDiam: 2.31,
    innerScale: 0.805,
    wobbleAmp: 0.003,
    wobbleFreq: 0.5,
    glassBase: '#000000',
    glassAlpha: 0.4,
    glassRough: 0.3,
    glassMicro: 0.5,
    glassIOR: 2,
    glassRefract: 0.88,
    glassTrans: 0.63,
    glassTint: '#00ffcc',
    ccIntensity: 0.4,
    ccRough: 0.05,
    glassEnv: 1,
    swirlSpeed: 3,
    noiseScale: 3,
    cloudDensity: 0.97,
    emissionStr: 0,
    scatterBlend: 0.87,
    hs1: 0,
    hs2: 0,
    hs3: 0,
    scatPurple: '#000000',
    scatTeal: '#0d6b4a',
    scatRed: '#cc2200',
    rimColor: '#000000',
    rimAlpha: 0,
    rimPower: 0.5,
    pulseMin: 0.1,
    pulseMax: 0.5,
    pulsePeriod: 0.5,
    hueDrift: 0,
    hueSpeed: 0.01,
    keyInt: 0.2,
    keyColor: '#ffe0a6',
    c1Int: 3,
    c1Color: '#ff8c00',
    c1Orbit: 1.1,
    c1Speed: 0.65,
    c2Int: 4.2,
    c2Color: '#4d26ff',
    c2Orbit: 4.3,
    c2Speed: 0.05,
    c3Int: 1.6,
    c3Color: '#00e6b3',
    c3Orbit: 2.8,
    c3Speed: 1.45,
    flickerAmp: 1.2,
    envInt: 0.5,
    bloomThresh: 0.65,
    bloomWeight: 3,
    bloomKernel: 256,
    chromAber: 0,
    exposure: 0.85,
    contrast: 1.95,
    discY: -3,
    discRadius: 1,
    discAlpha: 0,
    discSpin: -10,
  },
  conversation: {
    outerDiam: 2.31,
    innerScale: 0.8,
    wobbleAmp: 0.02,
    wobbleFreq: 1.15,
    glassBase: '#043e26',
    glassAlpha: 0.335,
    glassRough: 0.3,
    glassMicro: 0.5,
    glassIOR: 2,
    glassRefract: 0.88,
    glassTrans: 1,
    glassTint: '#41a018',
    ccIntensity: 0.38,
    ccRough: 0.05,
    glassEnv: 1.1,
    swirlSpeed: 2,
    noiseScale: 5.3,
    cloudDensity: 0.55,
    emissionStr: 1.55,
    scatterBlend: 1,
    hs1: 0,
    hs2: 0,
    hs3: 0,
    scatPurple: '#13fb59',
    scatTeal: '#219711',
    scatRed: '#bf1d45',
    rimColor: '#e60505',
    rimAlpha: 0,
    rimPower: 0.5,
    pulseMin: 0.1,
    pulseMax: 0.5,
    pulsePeriod: 0.5,
    hueDrift: 0,
    hueSpeed: 0.01,
    keyInt: 4.6,
    keyColor: '#f0190a',
    c1Int: 3,
    c1Color: '#ff8c00',
    c1Orbit: 1.1,
    c1Speed: 0.05,
    c2Int: 4.2,
    c2Color: '#ff4524',
    c2Orbit: 4.1,
    c2Speed: 1.6,
    c3Int: 1.6,
    c3Color: '#1dd320',
    c3Orbit: 5,
    c3Speed: 2,
    flickerAmp: 0,
    envInt: 2,
    bloomThresh: 0.65,
    bloomWeight: 2.55,
    bloomKernel: 80,
    chromAber: 0,
    exposure: 0.85,
    contrast: 1.95,
    discY: -3,
    discRadius: 1,
    discAlpha: 0,
    discSpin: -10,
  },
  intervention: {
    outerDiam: 2.31,
    innerScale: 0.805,
    wobbleAmp: 0.016,
    wobbleFreq: 1.7,
    glassBase: '#043e25',
    glassAlpha: 0.05,
    glassRough: 0.3,
    glassMicro: 0.5,
    glassIOR: 2,
    glassRefract: 1,
    glassTrans: 1,
    glassTint: '#5c0505',
    ccIntensity: 0.4,
    ccRough: 0.05,
    glassEnv: 2,
    swirlSpeed: 2,
    noiseScale: 5.2,
    cloudDensity: 0.1,
    emissionStr: 0,
    scatterBlend: 1,
    hs1: 0,
    hs2: 0,
    hs3: 0,
    scatPurple: '#000001',
    scatTeal: '#901836',
    scatRed: '#cc2201',
    rimColor: '#ff0000',
    rimAlpha: 0,
    rimPower: 0.5,
    pulseMin: 0.1,
    pulseMax: 0.5,
    pulsePeriod: 0.5,
    hueDrift: 0,
    hueSpeed: 0.01,
    keyInt: 0.6,
    keyColor: '#f0190a',
    c1Int: 3,
    c1Color: '#ff8c00',
    c1Orbit: 1.1,
    c1Speed: 0.05,
    c2Int: 6,
    c2Color: '#4d26ff',
    c2Orbit: 4.1,
    c2Speed: 1.6,
    c3Int: 1.6,
    c3Color: '#f57d2e',
    c3Orbit: 2.8,
    c3Speed: 1.45,
    flickerAmp: 1.2,
    envInt: 0.55,
    bloomThresh: 0.52,
    bloomWeight: 2.55,
    bloomKernel: 16,
    chromAber: 0,
    exposure: 0.9,
    contrast: 1.95,
    discY: -3,
    discRadius: 1,
    discAlpha: 0,
    discSpin: -10,
  },
};

const COLOR_KEYS = [
  'glassBase',
  'glassTint',
  'scatPurple',
  'scatTeal',
  'scatRed',
  'rimColor',
  'keyColor',
  'c1Color',
  'c2Color',
  'c3Color',
];

function hexToRGB(hex, BABYLON) {
  return new BABYLON.Color3(
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255
  );
}

function hexToVec3(hex, BABYLON) {
  return new BABYLON.Vector3(
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255
  );
}

function lerpHex(a, b, t) {
  const ar = parseInt(a.slice(1, 3), 16),
    ag = parseInt(a.slice(3, 5), 16),
    ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16),
    bg = parseInt(b.slice(3, 5), 16),
    bb = parseInt(b.slice(5, 7), 16);
  const r = Math.round(ar + (br - ar) * t),
    g = Math.round(ag + (bg - ag) * t),
    bl = Math.round(ab + (bb - ab) * t);
  return '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + bl.toString(16).padStart(2, '0');
}

function registerShaders(BABYLON) {
  BABYLON.Effect.ShadersStore['nebulaInteriorVertexShader'] = `
  precision highp float;
  attribute vec3 position;attribute vec3 normal;
  uniform mat4 worldViewProjection;uniform mat4 world;
  varying vec3 vPos;varying vec3 vNormW;varying vec3 vWorldPos;
  void main(){
    vPos=position;vNormW=normalize(vec3(world*vec4(normal,0.0)));
    vWorldPos=vec3(world*vec4(position,1.0));gl_Position=worldViewProjection*vec4(position,1.0);
  }`;

  BABYLON.Effect.ShadersStore['nebulaInteriorFragmentShader'] = `
  precision highp float;
  varying vec3 vPos;varying vec3 vNormW;varying vec3 vWorldPos;
  uniform float time;uniform vec3 camPos;
  uniform float uNoiseScale;uniform float uSwirlSpeed;
  uniform vec3 uScatPurple;uniform vec3 uScatTeal;uniform vec3 uScatRed;
  uniform float uHS1;uniform float uHS2;uniform float uHS3;
  uniform float uEmissionStr;uniform float uScatterBlend;uniform float uCloudDensity;
  uniform float uHueDrift;uniform float uHueSpeed;
  uniform float uRimPulseMin;uniform float uRimPulseMax;uniform float uPulsePeriod;
  uniform vec3 uRimColor;uniform float uAudioLevel;
  vec3 h3(vec3 p){p=vec3(dot(p,vec3(127.1,311.7,74.7)),dot(p,vec3(269.5,183.3,246.1)),dot(p,vec3(113.5,271.9,124.6)));return -1.0+2.0*fract(sin(p)*43758.5453);}
  float gn(vec3 p){vec3 i=floor(p),f=fract(p),u=f*f*(3.0-2.0*f);return mix(mix(mix(dot(h3(i),f),dot(h3(i+vec3(1,0,0)),f-vec3(1,0,0)),u.x),mix(dot(h3(i+vec3(0,1,0)),f-vec3(0,1,0)),dot(h3(i+vec3(1,1,0)),f-vec3(1,1,0)),u.x),u.y),mix(mix(dot(h3(i+vec3(0,0,1)),f-vec3(0,0,1)),dot(h3(i+vec3(1,0,1)),f-vec3(1,0,1)),u.x),mix(dot(h3(i+vec3(0,1,1)),f-vec3(0,1,1)),dot(h3(i+vec3(1,1,1)),f-vec3(1,1,1)),u.x),u.y),u.z);}
  float fbm(vec3 p){float v=0.0,a=0.5;for(int i=0;i<6;i++){v+=a*gn(p);p=p*2.07+vec3(31.41,27.18,42.85);a*=0.49;}return v;}
  mat3 hueRotMat(float h){float c=cos(h),s=sin(h);return mat3(0.213+c*0.787-s*0.213,0.715-c*0.715-s*0.715,0.072-c*0.072+s*0.928,0.213-c*0.213+s*0.143,0.715+c*0.285+s*0.140,0.072-c*0.072-s*0.283,0.213-c*0.213-s*0.787,0.715-c*0.715+s*0.715,0.072+c*0.928+s*0.072);}
  void main(){
    float audioBoost=1.0+uAudioLevel*1.8;
    float a=time*uSwirlSpeed;
    float ca=cos(a),sa=sin(a);
    vec3 rp=vec3(ca*vPos.x-sa*vPos.z,vPos.y,sa*vPos.x+ca*vPos.z);
    vec3 p=rp*uNoiseScale;
    float n1=fbm(p),n2=fbm(p+vec3(n1*1.8,n1*0.9,time*0.04)),n3=fbm(p+vec3(n2*1.5,n2*1.2,time*0.03));
    float nv=n3*0.5+0.5,ny=n2*0.5+0.5,normY=vPos.y*0.5+0.5,normX=vPos.x*0.5+0.5;
    vec3 magenta=vec3(0.878,0.251,0.984),pink=vec3(0.957,0.557,0.694),cyan=vec3(0.0,0.898,1.0),goldC=vec3(1.0,0.839,0.0);
    float tx=clamp(normX+n2*0.25,0.0,1.0);
    vec3 topArc=mix(mix(magenta,pink,smoothstep(0.0,0.4,tx)),mix(cyan,goldC,smoothstep(0.6,1.0,tx)),smoothstep(0.35,0.65,tx));
    vec3 tealC=mix(vec3(0.0,0.302,0.251),vec3(0.0,0.412,0.361),nv);
    vec3 coreC=mix(vec3(0.051,0.051,0.102),vec3(0.102,0.0,0.200),ny);
    vec3 rustC=mix(vec3(0.545,0.145,0.0),vec3(0.749,0.251,0.0),nv);
    vec3 botC=mix(mix(vec3(1.0,0.702,0.0),vec3(1.0,0.561,0.0),nv),vec3(0.902,0.318,0.0),smoothstep(0.4,0.0,normY));
    vec3 col=coreC;
    col=mix(col,botC,smoothstep(0.38,0.0,normY)*(0.5+nv*0.6));
    col=mix(col,tealC,smoothstep(0.5,0.0,normX)*smoothstep(0.15,0.65,normY)*(0.4+nv*0.7));
    col=mix(col,rustC,smoothstep(0.5,1.0,normX)*smoothstep(0.15,0.65,normY)*(0.4+nv*0.7));
    col=mix(col,topArc,smoothstep(0.5,0.88,normY)*(0.55+n1*0.25));
    vec3 scatter=mix(uScatPurple,mix(uScatTeal,uScatRed,nv),smoothstep(0.25,0.75,ny));
    col=mix(col,scatter,smoothstep(0.3,0.7,ny)*uScatterBlend*(0.4+nv*0.6));
    float hs1=smoothstep(0.62,1.0,fbm(rp*4.5+vec3(1.3,2.1,0.5+time*0.022)));
    float hs2=smoothstep(0.60,1.0,fbm(rp*3.8+vec3(-1.2,0.8,2.1+time*0.026)));
    float hs3=smoothstep(0.58,1.0,fbm(rp*5.2+vec3(0.5,-1.8,1.2+time*0.018)));
    col+=hs1*vec3(1.0,0.4,0.667)*uHS1*uEmissionStr*audioBoost;
    col+=hs2*vec3(0.0,1.0,0.8)*uHS2*uEmissionStr*audioBoost;
    col+=hs3*vec3(1.0,0.843,0.0)*uHS3*uEmissionStr*audioBoost;
    vec3 viewDir=normalize(camPos-vWorldPos);
    float NdotV=max(0.0,dot(normalize(vNormW),viewDir));
    float fresnel=pow(1.0-NdotV,2.8);
    float omega=6.28318/uPulsePeriod;
    float rimPulse=mix(uRimPulseMin,uRimPulseMax,(sin(time*omega)+1.0)*0.5)*(1.0+uAudioLevel*3.5);
    col+=fresnel*rimPulse*uRimColor*2.5;
    float hShift=sin(time*uHueSpeed)*radians(uHueDrift);
    col=hueRotMat(hShift)*col;col=max(col,vec3(0.0));
    float alpha=clamp(uCloudDensity+nv*0.35+fresnel*0.20,0.0,1.0);
    gl_FragColor=vec4(col,alpha);
  }`;
}

function buildBgCanvas() {
  const S = 2048;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#02000d';
  ctx.fillRect(0, 0, S, S);
  let seed = 9301;
  const rng = () => {
    seed = (seed * 49297 + 233987) % 233280;
    return seed / 233280;
  };
  for (let i = 0; i < 2800; i++) {
    const x = rng() * S,
      y = rng() * S,
      r = 0.3 + rng() * 1.4,
      b = 0.35 + rng() * 0.65,
      warm = rng() < 0.12;
    ctx.globalAlpha = b;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = warm ? `hsl(35,80%,92%)` : `hsl(0,0%,${82 + rng() * 18}%)`;
    ctx.fill();
    if (b > 0.82) {
      ctx.globalAlpha = 0.12;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r * 7);
      g.addColorStop(0, warm ? 'rgba(255,210,120,1)' : 'rgba(200,220,255,1)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.beginPath();
      ctx.arc(x, y, r * 7, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
  [
    { cx: 0.3, cy: 0.4, rx: 480, ry: 280, rot: -0.3, c: 'rgba(130,50,0,0.22)' },
    { cx: 0.7, cy: 0.6, rx: 400, ry: 240, rot: 0.4, c: 'rgba(80,20,160,0.20)' },
    { cx: 0.5, cy: 0.5, rx: 320, ry: 200, rot: 0.0, c: 'rgba(200,100,0,0.14)' },
    { cx: 0.2, cy: 0.7, rx: 280, ry: 180, rot: 0.6, c: 'rgba(0,100,80,0.12)' },
    { cx: 0.8, cy: 0.3, rx: 300, ry: 160, rot: -0.5, c: 'rgba(160,30,80,0.16)' },
  ].forEach((b) => {
    ctx.save();
    ctx.translate(b.cx * S, b.cy * S);
    ctx.rotate(b.rot);
    ctx.scale(1, b.ry / b.rx);
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, b.rx);
    g.addColorStop(0, b.c);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.beginPath();
    ctx.arc(0, 0, b.rx, 0, Math.PI * 2);
    ctx.fillStyle = g;
    ctx.fill();
    ctx.restore();
  });
  return c;
}

/**
 * @param {HTMLElement} root
 * @param {{ embedReadingSession?: boolean }} [options] When true, hide Start/End Call and tap audio from ReadingSession’s LiveKit room via setLiveKitRoom().
 * @returns {{ dispose: () => Promise<void>, setLiveKitRoom: (import('livekit-client').Room | null) => void }}
 */
export function mountNebulaOrb(root, options = {}) {
  const { embedReadingSession = false } = options;

  const BABYLON = window.BABYLON;
  if (!BABYLON) {
    throw new Error('Babylon.js must be loaded before mountNebulaOrb');
  }

  registerShaders(BABYLON);

  const $ = (sel) => root.querySelector(sel);

  // Transcripts/debug/visualizer are UI; this orb is now render-only.
  const transcriptsEnabled = false;
  const debugEnabled = false;

  const loadingEl = $('.nebulaOrb_loading');
  const connectBtn = $('.nebulaOrb_connectBtn');
  const disconnectBtn = $('.nebulaOrb_disconnectBtn');
  const statusEl = $('.nebulaOrb_status');

  const hintEl = $('.nebulaOrb_hint');
  const stateFlashEl = $('.nebulaOrb_stateFlash');
  const sidebarEl = $('.nebulaOrb_sidebar');
  const transcriptLabelEl = $('.nebulaOrb_transcriptLabel');
  const transcripts = $('.nebulaOrb_transcripts');
  const debugDiv = $('.nebulaOrb_debug');
  const vizCanvas = $('.nebulaOrb_visualizer');
  const audioMount = $('.nebulaOrb_audioMount');
  const vizCtx = vizCanvas ? vizCanvas.getContext('2d') : null;

  let currentP = Object.assign({}, STATES.neutral);
  let targetState = 'neutral';
  const LERP_SPEED = 0.035;

  function lerpToTarget(dt) {
    const target = STATES[targetState];
    const alpha = Math.min(1, LERP_SPEED * 60 * dt);
    for (const key in target) {
      if (key === 'wobbleAmp' || key === 'wobbleFreq') {
        currentP[key] = target[key];
      } else if (COLOR_KEYS.includes(key)) {
        currentP[key] = lerpHex(currentP[key], target[key], alpha);
      } else {
        currentP[key] = currentP[key] + (target[key] - currentP[key]) * alpha;
      }
    }
  }

  const audioState = {
    level: 0,
    analyser: null,
    vizAnalyser: null,
    context: null,
    mediaStreamSource: null,
    elementSource: null,
    _lastAmpLog: 0,
    _logFreqBuf: null,
  };

  let ownedRoom = null;
  let externalRoom = null;
  const externalHandlers = { sub: null, unsub: null, data: null };

  let vizRunning = false;
  let vizRaf = 0;

  function log(msg) {
    // Console logs are still useful during development.
    console.log(msg);
    if (!debugEnabled) return;
    if (!debugDiv) return;
    debugDiv.innerHTML = msg + '<br>' + debugDiv.innerHTML;
  }

  function pickBestRemoteAudioTrack(room) {
    if (!room) return null;
    let fallback = null;
    for (const participant of room.remoteParticipants.values()) {
      const id = participant.identity || '';
      const isAgent = id.includes('agent') || id.startsWith('agent');
      for (const pub of participant.trackPublications.values()) {
        if (pub.kind !== Track.Kind.Audio || !pub.track) continue;
        if (isAgent) return pub.track;
        fallback = fallback || pub.track;
      }
    }
    return fallback;
  }

  function clearAudioPipeline() {
    audioState._lastAmpLog = 0;
    audioState._logFreqBuf = null;
    audioState.level = 0;
    try {
      if (audioState.mediaStreamSource) {
        audioState.mediaStreamSource.disconnect();
        audioState.mediaStreamSource = null;
      }
      if (audioState.elementSource) {
        audioState.elementSource.disconnect();
        audioState.elementSource = null;
      }
    } catch (_) {
      /* ignore */
    }
    audioState.analyser = null;
    audioState.vizAnalyser = null;
    vizRunning = false;
    if (vizRaf) {
      cancelAnimationFrame(vizRaf);
      vizRaf = 0;
    }
    if (audioMount) audioMount.innerHTML = '';
    if (vizCanvas && vizCtx) {
      const w = vizCanvas.width;
      const h = vizCanvas.height;
      if (w && h) vizCtx.clearRect(0, 0, w, h);
    }
  }

  function reevaluateExternalAudio(room) {
    const t = pickBestRemoteAudioTrack(room);
    if (t) setupAudioFromRemoteTrack(t);
    else clearAudioPipeline();
  }

  function unbindLiveKitRoom() {
    if (externalRoom && externalHandlers.sub) {
      externalRoom.off(RoomEvent.TrackSubscribed, externalHandlers.sub);
      externalRoom.off(RoomEvent.TrackUnsubscribed, externalHandlers.unsub);
      externalRoom.off(RoomEvent.DataReceived, externalHandlers.data);
    }
    externalRoom = null;
    externalHandlers.sub = null;
    externalHandlers.unsub = null;
    externalHandlers.data = null;
    clearAudioPipeline();
  }

  function setLiveKitRoom(room) {
    if (room === externalRoom) {
      if (room) reevaluateExternalAudio(room);
      return;
    }
    unbindLiveKitRoom();
    if (!room) return;
    externalRoom = room;
    externalHandlers.sub = (track) => {
      if (track.kind === Track.Kind.Audio) reevaluateExternalAudio(room);
    };
    externalHandlers.unsub = (track) => {
      if (track.kind === Track.Kind.Audio) reevaluateExternalAudio(room);
    };
    // 👇 add this
    externalHandlers.data = (payload) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg.type === 'orb_state' && STATES[msg.state]) {
          log(`🎨 Orb state → ${msg.state}`);
          setActiveState(msg.state);
        }
      } catch (_) {}
    };
    room.on(RoomEvent.TrackSubscribed, externalHandlers.sub);
    room.on(RoomEvent.TrackUnsubscribed, externalHandlers.unsub);
    room.on(RoomEvent.DataReceived, externalHandlers.data); // 👈 add
    reevaluateExternalAudio(room);
    if (embedReadingSession) {
      log('✓ Orb bound to reading session LiveKit room');
    }
  }

  async function connect() {
    if (embedReadingSession) return;
    if (!connectBtn || !disconnectBtn || !statusEl || !audioMount) return;
    connectBtn.disabled = true;
    statusEl.textContent = 'Connecting…';
    try {
      const { token, url } = await fetchLiveKitToken();
      ownedRoom = new Room();
      ownedRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
        statusEl.textContent = `Connection: ${state}`;
        if (state === ConnectionState.Connected) {
          connectBtn.style.display = 'none';
          disconnectBtn.style.display = 'block';
          log('✓ Connected');
        }
      });
      ownedRoom.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
        if (track.kind === Track.Kind.Audio) {
          log(`✓ Audio from ${participant.identity}`);
          const el = track.attach();
          el.style.display = 'none';
          audioMount.appendChild(el);
          setupAudio(el);
        }
      });
      ownedRoom.on(RoomEvent.TranscriptionReceived, (segs, participant) => {
        segs.forEach((s) => {
          if (s.final) addTranscript(s.text, participant?.identity?.includes('agent') ? 'agent' : 'user');
        });
      });
      ownedRoom.on(RoomEvent.DataReceived, (payload, participant) => {
        try {
          const d = JSON.parse(new TextDecoder().decode(payload));
          if (d.type === 'transcription') addTranscript(d.text, participant.identity.startsWith('agent') ? 'agent' : 'user');
        } catch (_) {}
      });
      await ownedRoom.connect(url, token);
      await ownedRoom.localParticipant.setMicrophoneEnabled(true);
      statusEl.textContent = 'Connected — Speak now!';
      log('✓ Mic enabled');
    } catch (err) {
      statusEl.textContent = `Error: ${err.message}`;
      log(`✗ ${err.message}`);
      connectBtn.disabled = false;
    }
  }

  async function disconnectStandalone() {
    if (ownedRoom) {
      await ownedRoom.disconnect();
      ownedRoom = null;
    }
    clearAudioPipeline();
    if (embedReadingSession) return;
    if (!connectBtn || !disconnectBtn || !statusEl) return;
    connectBtn.disabled = false;
    connectBtn.style.display = 'block';
    disconnectBtn.style.display = 'none';
    statusEl.textContent = 'Disconnected';
  }

  function setupAudioFromRemoteTrack(track) {
    clearAudioPipeline();
    if (!track?.mediaStreamTrack) {
      log('✗ No MediaStreamTrack for orb audio');
      return;
    }
    if (!audioState.context) audioState.context = new AudioContext();
    const ctx = audioState.context;
    const stream = new MediaStream([track.mediaStreamTrack]);
    const source = ctx.createMediaStreamSource(stream);
    audioState.mediaStreamSource = source;
    audioState.analyser = ctx.createAnalyser();
    audioState.analyser.fftSize = 1024;
    audioState.analyser.smoothingTimeConstant = 0.6;
    source.connect(audioState.analyser);
    /* ReadingSession already plays agent audio — do not connect to ctx.destination. */
    log('✓ Orb audio analysis (shared LiveKit room)');
  }

  function setupAudio(audioEl) {
    clearAudioPipeline();
    if (!audioState.context) audioState.context = new AudioContext();
    const ctx = audioState.context;
    const source = ctx.createMediaElementSource(audioEl);
    audioState.elementSource = source;
    audioState.analyser = ctx.createAnalyser();
    audioState.analyser.fftSize = 1024;
    audioState.analyser.smoothingTimeConstant = 0.6;
    source.connect(audioState.analyser);
    audioState.analyser.connect(ctx.destination);
    log('✓ Audio ready');
  }

  function drawViz() {
    if (!vizCanvas || !vizCtx) return;
    if (!vizRunning || !audioState.vizAnalyser) return;
    vizRaf = requestAnimationFrame(drawViz);
    const buf = new Uint8Array(audioState.vizAnalyser.frequencyBinCount);
    audioState.vizAnalyser.getByteFrequencyData(buf);
    vizCtx.fillStyle = 'rgba(2,0,13,0.7)';
    vizCtx.fillRect(0, 0, vizCanvas.width, vizCanvas.height);
    const bw = (vizCanvas.width / buf.length) * 2.5;
    buf.forEach((v, i) => {
      const h = (v / 255) * vizCanvas.height;
      vizCtx.fillStyle = `rgba(255,${80 + Math.floor(v * 0.7)},30,0.8)`;
      vizCtx.fillRect(i * (bw + 1), vizCanvas.height - h, bw, h);
    });
  }

  function addTranscript(text, type) {
    if (!transcriptsEnabled) return;
    if (!transcripts) return;
    const div = document.createElement('div');
    div.className = type === 'agent' ? 'nebulaOrb_transcript nebulaOrb_transcriptAgent' : 'nebulaOrb_transcript nebulaOrb_transcriptUser';
    div.textContent = `${type === 'agent' ? '◈ Orb' : '◉ You'}: ${text}`;
    transcripts.appendChild(div);
    transcripts.scrollTop = transcripts.scrollHeight;
  }

  const FLASH_COLORS = {
    neutral: 'rgba(0,200,180,0.07)',
    conversation: 'rgba(30,200,60,0.07)',
    intervention: 'rgba(220,40,40,0.12)',
  };

  function setActiveState(name) {
    if (name === targetState) return;
    targetState = name;
    // UI toggling removed: state is reflected by the nebula shader only.
  }

  const uiAbort = new AbortController();
  const uiSignal = uiAbort.signal;

  // UI event listeners removed (render-only orb).

  function initOrb() {
    const canvas = $('.nebulaOrb_renderCanvas');
    const engine = new BABYLON.Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true, adaptToDeviceRatio: true });
    engine.setSize(canvas.clientWidth, canvas.clientHeight);
    const scene = new BABYLON.Scene(engine);
    scene.clearColor = new BABYLON.Color4(0, 0, 0, 1);

    const camera = new BABYLON.ArcRotateCamera('cam', -Math.PI / 2, Math.PI / 2.6, 5, BABYLON.Vector3.Zero(), scene);
    // Per request: no mouse/touch rotate/zoom; and always stay at max zoom.
    const MAX_RADIUS = 3;
    camera.lowerRadiusLimit = MAX_RADIUS;
    camera.upperRadiusLimit = MAX_RADIUS;
    camera.radius = MAX_RADIUS;
    // Do NOT attach controls (removes wheel/drag interactions).

    const bgTex = BABYLON.Texture.LoadFromDataString(
      'bg',
      buildBgCanvas().toDataURL('image/png'),
      scene,
      false,
      false,
      BABYLON.Texture.BILINEAR_SAMPLINGMODE
    );
    const bgLayer = new BABYLON.Layer('bg', null, scene, true);
    bgLayer.texture = bgTex;
    bgLayer.isBackground = true;

    const envTex = BABYLON.CubeTexture.CreateFromPrefilteredData('https://assets.babylonjs.com/environments/environmentSpecular.env', scene);
    scene.environmentTexture = envTex;

    const hemi = new BABYLON.HemisphericLight('h', new BABYLON.Vector3(0, 1, 0), scene);
    hemi.intensity = 0.12;
    hemi.diffuse = new BABYLON.Color3(0.8, 0.5, 0.2);

    const key = new BABYLON.DirectionalLight('key', new BABYLON.Vector3(-1, -1.5, -1), scene);
    const caus1 = new BABYLON.PointLight('c1', new BABYLON.Vector3(1.5, 0.5, -2), scene);
    const caus2 = new BABYLON.PointLight('c2', new BABYLON.Vector3(-2, -0.5, 1.5), scene);
    const caus3 = new BABYLON.PointLight('c3', new BABYLON.Vector3(0, 2, 1), scene);

    const innerSphere = BABYLON.MeshBuilder.CreateSphere('inner', { diameter: 2.31 * 0.805, segments: 96 }, scene);
    innerSphere.alphaIndex = 0;
    const nebulaMat = new BABYLON.ShaderMaterial(
      'nebulaMat',
      scene,
      { vertex: 'nebulaInterior', fragment: 'nebulaInterior' },
      {
        attributes: ['position', 'normal'],
        uniforms: [
          'worldViewProjection',
          'world',
          'time',
          'camPos',
          'uNoiseScale',
          'uSwirlSpeed',
          'uScatPurple',
          'uScatTeal',
          'uScatRed',
          'uHS1',
          'uHS2',
          'uHS3',
          'uEmissionStr',
          'uScatterBlend',
          'uCloudDensity',
          'uHueDrift',
          'uHueSpeed',
          'uRimPulseMin',
          'uRimPulseMax',
          'uPulsePeriod',
          'uRimColor',
          'uAudioLevel',
        ],
      }
    );
    nebulaMat.backFaceCulling = false;
    nebulaMat.needAlphaBlending = () => true;
    nebulaMat.alphaMode = BABYLON.Engine.ALPHA_COMBINE;
    innerSphere.material = nebulaMat;

    const outerSphere = BABYLON.MeshBuilder.CreateSphere('outer', { diameter: 2.31, segments: 128 }, scene);
    outerSphere.alphaIndex = 1;
    const glassMat = new BABYLON.PBRMaterial('glass', scene);
    outerSphere.material = glassMat;

    const rimSphere = BABYLON.MeshBuilder.CreateSphere('rim', { diameter: 2.31 * 1.0125, segments: 48 }, scene);
    rimSphere.alphaIndex = 2;
    const rimMat = new BABYLON.StandardMaterial('rimMat', scene);
    rimMat.disableLighting = true;
    rimMat.backFaceCulling = true;
    rimMat.alphaMode = BABYLON.Engine.ALPHA_ADD;
    rimMat.emissiveFresnelParameters = new BABYLON.FresnelParameters();
    rimSphere.material = rimMat;

    const disc = BABYLON.MeshBuilder.CreateDisc('disc', { radius: 6, tessellation: 128 }, scene);
    disc.rotation.x = Math.PI / 2;
    disc.position.y = -3;
    const discMat = new BABYLON.StandardMaterial('discMat', scene);
    discMat.disableLighting = true;
    try {
      const img = new Image();
      img.onload = () => {
        const tc = document.createElement('canvas');
        tc.width = tc.height = 1024;
        const tctx = tc.getContext('2d');
        tctx.save();
        tctx.beginPath();
        tctx.arc(512, 512, 512, 0, Math.PI * 2);
        tctx.clip();
        tctx.drawImage(img, 0, 0, 1024, 1024);
        tctx.restore();
        const fg = tctx.createRadialGradient(512, 512, 280, 512, 512, 512);
        fg.addColorStop(0, 'rgba(0,0,0,0)');
        fg.addColorStop(0.72, 'rgba(0,0,0,0)');
        fg.addColorStop(1, 'rgba(0,0,0,1)');
        tctx.fillStyle = fg;
        tctx.fillRect(0, 0, 1024, 1024);
        const ft = BABYLON.Texture.LoadFromDataString('disc', tc.toDataURL('image/png'), scene, false, false, BABYLON.Texture.BILINEAR_SAMPLINGMODE);
        discMat.diffuseTexture = ft;
        discMat.emissiveTexture = ft;
        discMat.emissiveColor = new BABYLON.Color3(1, 1, 1);
        discMat.alpha = 0;
      };
      img.onerror = () => {
        discMat.emissiveColor = new BABYLON.Color3(0.55, 0.2, 0);
        discMat.alpha = 0;
      };
      img.src = `${PUBLIC}/Orion_Nebula.jpg`;
    } catch (e) {
      discMat.emissiveColor = new BABYLON.Color3(0.55, 0.2, 0);
      discMat.alpha = 0;
    }
    disc.material = discMat;

    const pp = new BABYLON.DefaultRenderingPipeline('pp', true, scene, [camera]);
    pp.bloomEnabled = true;
    pp.bloomScale = 0.8;
    pp.chromaticAberrationEnabled = true;
    pp.imageProcessingEnabled = true;
    pp.imageProcessing.toneMappingEnabled = true;
    pp.imageProcessing.toneMappingType = BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES;
    pp.fxaaEnabled = true;
    pp.samples = 4;

    const tmpBuf = new Uint8Array(1024);
    function getAudioLevel() {
      if (!audioState.analyser) return 0;
      audioState.analyser.getByteTimeDomainData(tmpBuf);
      let sum = 0;
      for (let i = 0; i < tmpBuf.length; i++) {
        const v = (tmpBuf[i] - 128) / 128;
        sum += v * v;
      }
      return Math.sqrt(sum / tmpBuf.length);
    }

    const t0 = performance.now();
    scene.registerBeforeRender(() => {
      const t = (performance.now() - t0) / 1000;
      const dt = engine.getDeltaTime() / 1000;

      // Extra safety: keep radius clamped even if Babylon internals change it.
      camera.radius = MAX_RADIUS;

      lerpToTarget(dt);
      const p = currentP;

      const raw = getAudioLevel();
      audioState.level = audioState.level * 0.85 + raw * 0.15;
      const lvl = Math.min(audioState.level * 4, 1.0);

      const GLASS_SHRINK = 0.9;
      const wA = p.wobbleAmp + lvl * 0.2;
      const wF = p.wobbleFreq * Math.PI * 2;
      const w0 = 1 + wA * Math.sin(t * wF),
        w1 = 1 + wA * Math.sin(t * wF + 1.2),
        w2 = 1 + wA * Math.sin(t * wF + 2.4);
      innerSphere.scaling.set(w0, w1, w2);
      outerSphere.scaling.set(w0 * GLASS_SHRINK, w1 * GLASS_SHRINK, w2 * GLASS_SHRINK);
      rimSphere.scaling.set(w0 * GLASS_SHRINK, w1 * GLASS_SHRINK, w2 * GLASS_SHRINK);

      glassMat.albedoColor = hexToRGB(p.glassBase, BABYLON);
      glassMat.metallic = 0;
      glassMat.roughness = p.glassRough;
      glassMat.alpha = p.glassAlpha;
      glassMat.alphaMode = BABYLON.Engine.ALPHA_COMBINE;
      glassMat.reflectivityColor = new BABYLON.Color3(0.95, 0.82, 0.55);
      glassMat.microSurface = p.glassMicro;
      glassMat.subSurface.isRefractionEnabled = true;
      glassMat.subSurface.isTranslucencyEnabled = true;
      glassMat.subSurface.refractionIntensity = p.glassRefract;
      glassMat.subSurface.translucencyIntensity = p.glassTrans;
      glassMat.subSurface.indexOfRefraction = p.glassIOR;
      glassMat.subSurface.tintColor = hexToRGB(p.glassTint, BABYLON);
      glassMat.clearCoat.isEnabled = true;
      glassMat.clearCoat.intensity = p.ccIntensity;
      glassMat.clearCoat.roughness = p.ccRough;
      glassMat.reflectionTexture = glassMat.reflectionTexture || envTex;
      glassMat.refractionTexture = envTex;
      glassMat.environmentIntensity = p.glassEnv;

      const rimC = hexToRGB(p.rimColor, BABYLON);
      rimMat.emissiveColor = rimC;
      rimMat.emissiveFresnelParameters.leftColor = rimC;
      rimMat.emissiveFresnelParameters.power = p.rimPower;
      rimMat.emissiveFresnelParameters.rightColor = BABYLON.Color3.Black();
      const omega = 6.28318 / p.pulsePeriod;
      rimMat.alpha = p.rimAlpha * ((p.pulseMin + p.pulseMax) / 2 + ((p.pulseMax - p.pulseMin) / 2) * Math.sin(t * omega)) * (1 + lvl * 2);

      key.intensity = p.keyInt;
      key.diffuse = hexToRGB(p.keyColor, BABYLON);
      caus1.diffuse = hexToRGB(p.c1Color, BABYLON);
      caus2.diffuse = hexToRGB(p.c2Color, BABYLON);
      caus3.diffuse = hexToRGB(p.c3Color, BABYLON);
      scene.environmentIntensity = p.envInt;

      const fl = p.flickerAmp;
      caus1.position.x = Math.sin(t * p.c1Speed) * p.c1Orbit;
      caus1.position.z = Math.cos(t * p.c1Speed) * p.c1Orbit;
      caus1.position.y = Math.sin(t * 1.1) * 0.6;
      caus1.intensity = p.c1Int + fl * Math.sin(t * 3.7) + lvl * 2;
      caus2.position.x = Math.cos(t * p.c2Speed) * p.c2Orbit;
      caus2.position.z = Math.sin(t * p.c2Speed) * p.c2Orbit;
      caus2.position.y = Math.cos(t * 0.9) * 0.9;
      caus2.intensity = p.c2Int + fl * 0.7 * Math.sin(t * 2.3 + 1.5) + lvl * 1.5;
      caus3.position.x = Math.sin(t * p.c3Speed) * p.c3Orbit;
      caus3.position.z = Math.cos(t * p.c3Speed) * p.c3Orbit;
      caus3.intensity = p.c3Int + fl * 0.6 * Math.sin(t * 1.8 + 0.8);

      pp.bloomThreshold = p.bloomThresh;
      pp.bloomWeight = p.bloomWeight;
      pp.bloomKernel = Math.round(p.bloomKernel);
      pp.chromaticAberration.aberrationAmount = p.chromAber;
      pp.imageProcessing.exposure = p.exposure;
      pp.imageProcessing.contrast = p.contrast;

      disc.position.y = p.discY;
      discMat.alpha = p.discAlpha;
      disc.rotation.z += dt * p.discSpin * 0.01745;

      nebulaMat.setFloat('time', t);
      nebulaMat.setVector3('camPos', camera.position);
      nebulaMat.setFloat('uNoiseScale', p.noiseScale);
      nebulaMat.setFloat('uSwirlSpeed', p.swirlSpeed * 0.01745);
      nebulaMat.setVector3('uScatPurple', hexToVec3(p.scatPurple, BABYLON));
      nebulaMat.setVector3('uScatTeal', hexToVec3(p.scatTeal, BABYLON));
      nebulaMat.setVector3('uScatRed', hexToVec3(p.scatRed, BABYLON));
      nebulaMat.setFloat('uHS1', p.hs1);
      nebulaMat.setFloat('uHS2', p.hs2);
      nebulaMat.setFloat('uHS3', p.hs3);
      nebulaMat.setFloat('uEmissionStr', p.emissionStr);
      nebulaMat.setFloat('uScatterBlend', p.scatterBlend);
      nebulaMat.setFloat('uCloudDensity', p.cloudDensity);
      nebulaMat.setFloat('uHueDrift', p.hueDrift);
      nebulaMat.setFloat('uHueSpeed', p.hueSpeed);
      nebulaMat.setFloat('uRimPulseMin', p.pulseMin);
      nebulaMat.setFloat('uRimPulseMax', p.pulseMax);
      nebulaMat.setFloat('uPulsePeriod', p.pulsePeriod);
      nebulaMat.setVector3('uRimColor', hexToVec3(p.rimColor, BABYLON));
      nebulaMat.setFloat('uAudioLevel', lvl);
    });

    engine.runRenderLoop(() => scene.render());

    const onResize = () => {
      engine.setSize(canvas.clientWidth, canvas.clientHeight);
      engine.resize();
    };
    window.addEventListener('resize', onResize);

    const tex = new BABYLON.Texture(`${PUBLIC}/ITZ.png`, scene, false, false);
    tex.coordinatesMode = BABYLON.Texture.SPHERICAL_MODE;
    glassMat.reflectionTexture = tex;
    glassMat.reflectionTexture.level = 1.2;

    return { engine, scene, onResize, glassMat };
  }

  let engine = null;
  let scene = null;
  let onResize = null;

  const r = initOrb();
  engine = r.engine;
  scene = r.scene;
  onResize = r.onResize;

  return {
    setLiveKitRoom,
    dispose: async () => {
      uiAbort.abort();
      vizRunning = false;
      if (vizRaf) cancelAnimationFrame(vizRaf);
      unbindLiveKitRoom();
      await disconnectStandalone();
      if (onResize) window.removeEventListener('resize', onResize);
      if (engine) {
        engine.stopRenderLoop();
        if (scene) scene.dispose();
        engine.dispose();
      }
    },
  };
}
